import { useRef, useState } from 'react';
import { submitRsvpPublic } from './api';
import type { RsvpStatus, SubmitRsvpPayload } from './types';

type State = 'idle' | 'loading' | 'success' | 'error';

export interface UseRsvpReturn {
  state: State;
  errorMessage: string | null;
  submit: (payload: SubmitRsvpPayload) => Promise<void>;
  reset: () => void;
}

export function useRsvp(invitationId: string | null | undefined, isLive: boolean): UseRsvpReturn {
  const [state, setState] = useState<State>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const submitting = useRef(false);

  const submit = async (payload: SubmitRsvpPayload) => {
    if (submitting.current) return;
    submitting.current = true;
    setErrorMessage(null);
    setState('loading');

    if (!isLive || !invitationId) {
      setState('success');
      submitting.current = false;
      return;
    }

    try {
      await submitRsvpPublic(invitationId, payload);
      setState('success');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not submit your RSVP. Please try again.';
      setErrorMessage(msg);
      setState('error');
    } finally {
      submitting.current = false;
    }
  };

  const reset = () => {
    submitting.current = false;
    setState('idle');
    setErrorMessage(null);
  };

  return { state, errorMessage, submit, reset };
}

// Re-export RsvpStatus for convenience when importing from this module
export type { RsvpStatus };
